import React, { useEffect, useState } from "react";
import { EmailTemplatesTable } from "../../components/tables/EmailTemplatesTable";
import {
  ButtonProps,
  EmptyState,
  ListX,
  LoadingIndicator,
} from "@surya-digital/leo-reactjs-material-ui";
import { useTranslation } from "react-i18next";
import { useEmailTemplatesStore } from "../../stores/Hooks";
import { Stack } from "@mui/material";
import { observer } from "mobx-react";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { Plus } from "lucide-react";
import { useSignInStore } from "../../../store/Hooks";
import { UserPrivileges } from "../../../store/user/UserPrivileges";

interface EmailTemplatesPageContentProps {
  onClickCreateEmailTemplateButton: VoidFunction;
  setOpenConfirmDeleteEmailTemplateDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const EmailTemplatesPageContent: React.FC<EmailTemplatesPageContentProps> =
  observer(
    ({
      onClickCreateEmailTemplateButton,
      setOpenConfirmDeleteEmailTemplateDialog,
    }) => {
      const [openUnhandledErrorDialog, setOpenUnhandledErrorDialog] =
        useState(false);

      const { t } = useTranslation();
      const emailTemplatesStore = useEmailTemplatesStore();
      const navigateTo = useRouteNavigator();
      const signInStore = useSignInStore();

      useEffect(() => {
        emailTemplatesStore
          .getEmailTemplates()
          .catch(() => setOpenUnhandledErrorDialog(true));
      }, []);

      if (openUnhandledErrorDialog) {
        return (
          <UnexpectedErrorDialog
            message={t(
              "emailTemplates.getEmailTemplatesUnexpectedErrorMessage",
            )}
            onClickDone={() => {
              setOpenUnhandledErrorDialog(false);
              navigateTo.project();
            }}
          />
        );
      }

      if (emailTemplatesStore.loadingEmailTemplates) {
        return (
          <LoadingIndicator
            variant="container"
            loadingText={t("common.searchTableLoadingState")}
            isLoading
          />
        );
      }

      const emptyStateButtonProps: ButtonProps | undefined =
        signInStore.isPrivilegeGranted(
          UserPrivileges.processGlobalEmailTemplate,
        )
          ? {
              name: "createEmailTemplate",
              label: t("emailTemplates.createEmailTemplate"),
              variant: "outlined-color",
              size: "small",
              icon: <Plus />,
              onClick: onClickCreateEmailTemplateButton,
            }
          : undefined;

      if (
        !emailTemplatesStore.emailTemplates.length &&
        !emailTemplatesStore.emptySearchQueryResult
      ) {
        return (
          <Stack justifyContent="center" height="100%">
            <EmptyState
              icon={<ListX />}
              title={t("emailTemplates.emptyStateTitle")}
              description={t("emailTemplates.emptyStateDescription")}
              width="100%"
              buttonProps={emptyStateButtonProps}
            />
          </Stack>
        );
      }

      return (
        <EmailTemplatesTable
          setOpenConfirmDeleteEmailTemplateDialog={
            setOpenConfirmDeleteEmailTemplateDialog
          }
        />
      );
    },
  );
